import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { ListingState } from '__generated__/athena/graphql';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import type { PriceCut } from 'components/marketplace/helpers/certaintyPanel';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import { Pill } from 'components/marketplace/shared/Pill';
import { isPropertyAvailableForPurchase } from 'components/properties/helpers/property';

import type { PropertyDetailsType } from '../types';
import { PriceCutHeadline } from './CertaintyPanel/PriceCutHeadline';
import { PricingTraditional } from './Pricing';

type PropertySummaryData = Pick<
  PropertyDetailsType,
  | 'bathrooms'
  | 'bedrooms'
  | 'buyDirectPrice'
  | 'canSelfTour'
  | 'city'
  | 'displayBadge'
  | 'estimatedMonthlyPayment'
  | 'hasCurrentAvailability'
  | 'isFavoriteLoading'
  | 'isLiked'
  | 'listPrice'
  | 'listingHistory'
  | 'listingState'
  | 'listingTags'
  | 'onToggleFavorite'
  | 'schoolDistricts'
  | 'sqFtTotalLiving'
  | 'state'
  | 'street'
  | 'unitNumber'
  | 'unitType'
  | 'zip'
>;

interface PropertySummaryProps {
  property: PropertySummaryData;
  // Certainty-panel treatment only; null leaves the summary untouched.
  priceCut?: PriceCut | null;
  // Certainty-panel treatment only; the freshness pill above the address.
  recentlyListed?: boolean;
}

export const PropertySummary = ({
  property,
  priceCut = null,
  recentlyListed = false,
}: PropertySummaryProps) => {
  const formattedStreet = formatStreetAddress(
    property.street,
    property.unitType,
    property.unitNumber,
  );
  const availableForPurchase = isPropertyAvailableForPurchase(property);
  const displayPrice = getDisplayPrice(property, 'listed');
  const locality = [property.city, property.state].filter(Boolean).join(', ');

  return (
    <Col>
      {recentlyListed && (
        <Col className="pb-4">
          <Pill label="Listed recently" rounded responsive />
        </Col>
      )}
      <Row className="items-start justify-between pb-6">
        <Text as="h1" className="text-subheader-lg">
          <Text as="span" className="text-subheader-sm md:text-subheader-lg">
            {formattedStreet}
          </Text>
          <br />
          <Text
            as="span"
            className="text-subheader-sm text-od-content-tertiary md:text-subheader-lg"
          >
            {[locality, property.zip].filter(Boolean).join(' ')}
          </Text>
        </Text>
        <Box className="rounded-2xl border border-od-border-primary bg-od-bg-primary p-3">
          <Button
            variant="secondary"
            aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
            analyticsName="cosmos-marketplace-pdp-favorite"
            onClick={property.onToggleFavorite}
            loading={property.isFavoriteLoading}
            className="size-9 rounded-full border-od-border-tertiary p-0"
          >
            {property.isLiked ? (
              <IconHeartFilled size={24} className="text-od-content-error" />
            ) : (
              <IconHeart size={24} />
            )}
          </Button>
        </Box>
      </Row>
      <Col as="section">
        {priceCut && displayPrice != null ? (
          <PriceCutHeadline price={formatCurrency(displayPrice)} priceCut={priceCut} />
        ) : (
          <PricingTraditional
            price={displayPrice}
            isAvailableSoon={property.listingState === ListingState.AVAILABLE_SOON}
          />
        )}
        {property.listingState === ListingState.AVAILABLE_SOON ? (
          <Text as="p" className="pb-4 text-body-sm text-od-content-tertiary">
            {AVAILABLE_SOON_DISCLOSURE}
          </Text>
        ) : null}
        <Row>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.bedrooms} bds
          </Text>
          <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
            ·
          </Text>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.bathrooms} ba
          </Text>
          <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
            ·
          </Text>
          <Text as="span" className="text-body-md text-od-content-tertiary">
            {property.sqFtTotalLiving} sqft
          </Text>
          {availableForPurchase && property.estimatedMonthlyPayment && (
            <>
              <Text as="span" className="px-2 text-body-md text-od-content-tertiary">
                ·
              </Text>
              <Text className="text-body-md text-od-content-tertiary">
                {`${formatCurrency(property.estimatedMonthlyPayment)}/month est.`}
              </Text>
            </>
          )}
        </Row>
        {!!property.schoolDistricts.length && (
          <Row>
            <Text as="span" className="pt-1 text-body-md text-od-content-tertiary">
              {property.schoolDistricts.join(' & ')}
            </Text>
          </Row>
        )}
      </Col>
    </Col>
  );
};
